import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { getPropertyCoords } from '../services/coordinatesHelper';
import { formatPrice, buildPropertySlug, optimizedImageUrl } from '../services/propertyService';
import { MF_BASE } from '../basePath';

const VIDEO_EXTS = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
const isVideo = (url) => VIDEO_EXTS.test(url || '');

function pickCover(imagens) {
  if (!imagens?.length) return null;
  return imagens.find(u => !isVideo(u)) || imagens[0];
}

function buildIcon({ highlighted }) {
  const fill = '#B8935A'; // dourado MF
  const stroke = '#FFFFFF';
  // Pino grande + halo pulsante pra destacar do resto
  const html = `
    <div style="position:relative;width:48px;height:64px;pointer-events:none;">
      <div style="
        position:absolute;left:50%;top:46px;transform:translate(-50%,-50%);
        width:38px;height:38px;border-radius:50%;
        background:rgba(184,147,90,0.30);
        animation:mfPulse 1.8s ease-out infinite;
        pointer-events:none;
      "></div>
      <svg width="48" height="64" viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg"
        style="position:absolute;left:0;top:0;display:block;filter:drop-shadow(0 4px 8px rgba(0,0,0,0.45));cursor:pointer;pointer-events:auto;transition:transform 150ms ease;">
        <path d="M14 1 C6.8 1 1 6.8 1 14 C1 24 14 37 14 37 C14 37 27 24 27 14 C27 6.8 21.2 1 14 1 Z"
          fill="${fill}" stroke="${stroke}" stroke-width="2.2"/>
        <circle cx="14" cy="14" r="5.5" fill="${stroke}"/>
        <path d="M14 11 L11 14 L11 17 L13 17 L13 15 L15 15 L15 17 L17 17 L17 14 Z" fill="${fill}"/>
      </svg>
    </div>
    <style>
      @keyframes mfPulse {
        0% { transform: translate(-50%,-50%) scale(0.85); opacity: 0.55; }
        70% { transform: translate(-50%,-50%) scale(1.6); opacity: 0; }
        100% { transform: translate(-50%,-50%) scale(0.85); opacity: 0; }
      }
    </style>
  `;
  return L.divIcon({
    html,
    className: 'mf-pin-property',
    iconSize: [48, 64],
    iconAnchor: [24, 60],
    popupAnchor: [0, -56],
  });
}

function buildNearbyIcon(emoji) {
  const html = `
    <div style="
      width:26px;height:26px;border-radius:50%;
      background:rgba(255,255,255,0.92);
      border:1.5px solid rgba(184,147,90,0.7);
      display:flex;align-items:center;justify-content:center;
      font-size:13px;line-height:1;
      box-shadow:0 1px 2px rgba(0,0,0,.25);
      cursor:pointer;
      font-family:'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji',sans-serif;
    ">${emoji}</div>
  `;
  return L.divIcon({
    html,
    className: 'mf-pin-nearby',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -14],
  });
}

function FitBounds({ properties }) {
  const map = useMap();
  useEffect(() => {
    if (!properties.length) return;
    // Mapa sem tamanho (sidebar display:none no mobile) -> projecao gera NaN
    const size = map.getSize();
    if (!size || size.x === 0 || size.y === 0) return;
    const points = properties
      .map(p => {
        const c = getPropertyCoords(p);
        return [c.lat, c.lng];
      })
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
    } else {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
    }
  }, [properties, map]);
  return null;
}

export default function PropertyMap({ properties, highlightedId, onMarkerHover, nearbyPlaces = [] }) {
  const navigate = useNavigate();
  const markerRefs = useRef({});

  const points = useMemo(
    () =>
      properties
        .filter(p => p.bairro || p.cidade)
        .map(p => ({ property: p, coords: getPropertyCoords(p) }))
        // Nunca passar coordenada invalida pro Leaflet (NaN derruba o mapa)
        .filter(({ coords }) => Number.isFinite(coords.lat) && Number.isFinite(coords.lng)),
    [properties]
  );

  const mapRef = useRef(null);

  // Pan + open popup do pin destacado
  useEffect(() => {
    if (!highlightedId) return;
    const marker = markerRefs.current[highlightedId];
    const map = mapRef.current;
    if (!marker || !map) return;
    // No mobile a sidebar do mapa fica display:none (0x0) mas montada. Um
    // toque no card dispara mouseenter -> highlightedId, e flyTo num mapa
    // sem tamanho gera LatLng NaN e derruba a pagina. So mexe se tiver tamanho.
    const size = map.getSize();
    if (!size || size.x === 0 || size.y === 0) return;
    const latlng = marker.getLatLng();
    if (!latlng || !Number.isFinite(latlng.lat) || !Number.isFinite(latlng.lng)) return;
    const currentZoom = map.getZoom();
    const targetZoom = Number.isFinite(currentZoom) ? Math.max(currentZoom, 14) : 14;
    map.flyTo(latlng, targetZoom, { duration: 0.6 });
    marker.openPopup();
  }, [highlightedId]);

  if (!points.length) {
    return (
      <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#F4F4F2', color: '#535C70', fontSize: 14,
      }}>
        Nenhum imóvel pra exibir no mapa
      </div>
    );
  }

  const center = [points[0].coords.lat, points[0].coords.lng];

  return (
    <MapContainer
      center={center}
      zoom={13}
      style={{ width: '100%', height: '100%' }}
      scrollWheelZoom
      ref={mapRef}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds properties={properties} />
      {points.map(({ property, coords }) => (
        <Marker
          key={property.id}
          position={[coords.lat, coords.lng]}
          icon={buildIcon({
            highlighted: highlightedId === property.id,
          })}
          zIndexOffset={1000}
          ref={(ref) => {
            if (ref) markerRefs.current[property.id] = ref;
          }}
          eventHandlers={{
            mouseover: () => onMarkerHover?.(property.id),
            mouseout: () => onMarkerHover?.(null),
          }}
        >
          <Popup>
            <div style={{ width: 200, fontFamily: 'Inter, sans-serif' }}>
              {(() => {
                const cover = pickCover(property.imagens);
                if (!cover) return null;
                const style = {
                  width: '100%', height: 110, objectFit: 'cover',
                  borderRadius: 4, marginBottom: 8, display: 'block',
                };
                return isVideo(cover) ? (
                  <video
                    src={`${cover}#t=0.1`}
                    muted
                    playsInline
                    preload="metadata"
                    style={style}
                  />
                ) : (
                  <img src={optimizedImageUrl(cover, { width: 300 })} alt={property.titulo} style={style} />
                );
              })()}
              <div style={{ fontSize: 15, fontWeight: 600, color: '#171717', marginBottom: 4 }}>
                {formatPrice(property.preco)}
              </div>
              <div style={{ fontSize: 12, color: '#535C70', marginBottom: 8, lineHeight: 1.4 }}>
                {property.titulo}
              </div>
              <button
                onClick={() => { window.location.href = `${MF_BASE}/imoveis/${buildPropertySlug(property)}`; }}
                style={{
                  width: '100%', padding: '8px 12px',
                  background: '#0B1929', color: '#FFFFFF',
                  border: 'none', borderRadius: 4,
                  fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                Ver imóvel
              </button>
            </div>
          </Popup>
        </Marker>
      ))}
      {nearbyPlaces.map(p => (
        <Marker
          key={`np-${p.id}`}
          position={[p.lat, p.lng]}
          icon={buildNearbyIcon(p.icon)}
        >
          <Popup>
            <div style={{ width: 180, fontFamily: 'Inter, sans-serif' }}>
              <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                {p.icon} {p.categoryLabel}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#171717', marginBottom: 6, lineHeight: 1.3 }}>
                {p.name}
              </div>
              <div style={{ fontSize: 12, color: '#B8935A', fontWeight: 600 }}>
                {p.distance < 1 ? `${Math.round(p.distance * 1000)} m` : `${p.distance.toFixed(1)} km`} do imóvel
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
